import type { Config } from '@puckeditor/core';

import {
  COMMERCE_BLOCK_NAMES,
  NAV_BLOCK_NAMES,
  COMMERCE_BLOCKS,
  PAGE_BLOCKS,
  PAGE_CONTENT_BLOCK_NAMES,
  PAGE_LAYOUT_BLOCK_NAMES,
} from '../components/blocks';
import { faqConfig } from '../components/faq';
import { linkConfig } from '../components/link';
import { PathField } from '../fields';
import { createPageRootConfig } from '../components/page-root-config';

// -- Config --

/** Puck editor configuration for the standalone page builder. */
export const standalonePagePuckConfig: Config = {
  categories: {
    layout: {
      title: 'Layout',
      components: [...PAGE_LAYOUT_BLOCK_NAMES, 'Faq'],
    },
    content: {
      title: 'Content',
      components: [...PAGE_CONTENT_BLOCK_NAMES, 'Link'],
    },
    productCarousels: {
      title: 'Product Carousels',
      components: COMMERCE_BLOCK_NAMES,
    },
    navCarousels: {
      title: 'Category & Collection Carousels',
      components: NAV_BLOCK_NAMES,
    },
  },
  root: createPageRootConfig({
    fields: {
      // A plain text field (not a custom widget like urlPath/PathField -- no format to validate)
      // so the page's display name can be edited at any time, independent of its variantKey/URL
      // slug. Seeded once from VariantNameModal's typed display name on first publish (see
      // `CmsEditorShell.handleModalConfirm`); editable here afterwards. Read via `getName(data)`
      // in `standalone-page-editor/editor.tsx` and sent as the pages route's required `name`
      // field alongside `urlPath` on every save/publish.
      name: {
        type: 'text',
        label: 'Page Name',
        ai: { exclude: true },
      },
      urlPath: {
        type: 'custom',
        label: 'URL Path',
        render: PathField,
        ai: {
          stream: false,
          schema: { type: 'string', pattern: '^/[a-z0-9]+(?:[/\\-_][a-z0-9]+)*/?$' },
          instructions:
            'Relative URL path for this page. Must start with /. Lowercase letters, numbers, hyphens, underscores only. e.g. /about, /deals/summer-2026',
        },
      },
    },
    defaultProps: { name: '', urlPath: '' },
    ai: {
      instructions:
        "You are building a full standalone web page for a cannabis dispensary. A new page leads with a single Hero: a page-leading banner holding its own Heading, Paragraph and Button in its content slot. Keep one Hero, at the top, and put the page's headline in it. Below the Hero, every block that holds content must sit inside a Section — never leave a Section empty. Structure: add a Section, then add Heading, Paragraph, Image, or Button components INTO that Section's content slot. Use FlexLayout inside a Section when you need side-by-side columns. Faq is a page-level block: place it directly on the page rather than inside a Section, and use it when the content is a list of questions and answers. Prefer clean, modern designs with adequate spacing between sections.",
    },
  }),
  components: { ...PAGE_BLOCKS, ...COMMERCE_BLOCKS, Link: linkConfig, Faq: faqConfig },
};
